import {
  HttpErrorResponse,
  HttpEventType,
  HttpInterceptorFn,
} from "@angular/common/http";
import { tap } from "rxjs";
import { AuthService } from "@core/services/auth/auth.service";
import { inject } from "@angular/core";
import { MessageService } from "primeng/api";
import { configuration } from "properties/config";
import {
  SKIP_GLOBAL_ERROR,
  SHOW_BACKEND_ERROR_MESSAGE,
} from "@core/interceptors/http-context-tokens";
import { LoggerService } from "@core/logging/logger.service";
import { LogContext } from "@core/logging/logger.types";
import { stripQueryAndFragment } from "@core/logging/navigation-breadcrumbs.service";
import { PerformanceMonitorService } from "@core/logging/performance-monitor.service";
import { TranslocoService } from "@jsverse/transloco";
import { translateOr } from "@core/services/translation/translate-or.util";
import { TranslationService } from "@core/services/translation/translation.service";

// Interface for API error response format
interface ApiErrorResponse {
  success: boolean;
  data: null;
  message: string;
  meta: {
    error_type: string;
    status_code: number;
  };
}

// Matches a path segment that is a numeric id, a UUID, or a long hex/opaque id.
// Used to collapse ids into ":id" so the RUM route label stays low-cardinality
// and never carries a resource identifier off-device (PII rules, Tickets 1/2/4).
// UUIDs and long hex ids share one hex+hyphen branch (>= 16 chars) to keep the
// pattern simple.
const ID_SEGMENT = /^(?:\d+|[0-9a-f-]{16,})$/i;

const MAX_ROUTE_LENGTH = 120;

/**
 * Reduce a request URL to a PII-free, low-cardinality route template:
 * path only (no origin, no query/fragment), id segments replaced with ":id".
 * Exported for unit testing.
 */
export function toApiRouteTemplate(url: string): string {
  let path: string;
  try {
    path = new URL(url).pathname;
  } catch {
    path = stripQueryAndFragment(url);
  }
  const template = path
    .split("/")
    .map((segment) => (ID_SEGMENT.test(segment) ? ":id" : segment))
    .join("/");
  return template.length > MAX_ROUTE_LENGTH
    ? template.slice(0, MAX_ROUTE_LENGTH)
    : template;
}

// Shared state needed by the global error handler below.
interface ErrorToastContext {
  messageService: MessageService;
  authService: AuthService;
  transloco: TranslocoService;
  translationService: TranslationService;
  log: LogContext;
  requestUrl: string;
  errorMessage: string | undefined;
  showBackendErrorMessage: boolean;
}

// Extract the backend-provided message only when the component opted in.
function extractBackendMessage(
  err: HttpErrorResponse,
  showBackendErrorMessage: boolean,
  log: LogContext
): string | undefined {
  try {
    const errorBody = err.error as ApiErrorResponse;
    if (
      showBackendErrorMessage &&
      errorBody &&
      typeof errorBody === "object" &&
      errorBody.message
    ) {
      return errorBody.message;
    }
  } catch (parseError) {
    log.debug("Failed to parse error response body", parseError);
  }
  return undefined;
}

// Show the appropriate global error toast (and force logout on 401) based on
// the status code. Extracted from the interceptor to keep its complexity low.
function handleGlobalError(err: HttpErrorResponse, ctx: ErrorToastContext): void {
  const { messageService, authService, transloco, log, requestUrl } = ctx;
  const detail = (fallback: string) =>
    ctx.showBackendErrorMessage && ctx.errorMessage
      ? ctx.errorMessage
      : fallback;

  // Not logging out on 403
  // Prevent logout loop by checking if the failed request was already a logout call
  // Also skip logout for validate-token endpoint to prevent infinite loops
  if (
    err.status === 401 &&
    !requestUrl.includes(configuration.authUrls.logout) &&
    !requestUrl.includes(configuration.authUrls.validateToken)
  ) {
    messageService.add({
      key: "global",
      severity: "error",
      summary: translateOr(transloco, "ui.common.errors.SESSION_EXPIRED", "Session Expired. Please login again."),
      detail: detail("Authentication is required. Please try again later."),
      life: 7000,
    });
    authService.userLogout();
    return;
  }

  // Show appropriate error toast based on status code
  switch (err.status) {
    case 0:
      messageService.add({
        key: "global",
        severity: "error",
        summary: "Network Error",
        detail: translateOr(transloco, "ui.common.errors.NETWORK_UNAVAILABLE", "Unable to connect to the server. Please check your internet connection and try again."),
        life: 7000,
      });
      break;
    case 400:
      // Special handling for addCustomer API or when backend message is explicitly requested
      messageService.add({
        key: "global",
        severity: "error",
        summary: "Something Went Wrong. Please Try Again.",
        detail: detail(
          ctx.translationService.translateOr(
            "ui.common.errors.REQUEST_ISSUE",
            "There was some issue processing your request"
          )
        ),
        life: 7000,
      });
      break;
    case 401:
      messageService.add({
        key: "global",
        severity: "error",
        summary: translateOr(transloco, "ui.common.errors.SESSION_EXPIRED", "Session Expired. Please login again."),
        detail: detail("Authentication is required. Please try again later."),
        life: 7000,
      });
      break;
    case 403:
      if (err.headers.get("content-type")?.includes("text/html")) {
        messageService.add({
          key: "global",
          severity: "warn",
          summary: "Security Policy Violation",
          detail: translateOr(transloco, "ui.common.errors.SECURITY_POLICY_VIOLATION", "Your request couldn't be processed. Please double-check your input and try again."),
          life: 10000,
        });
      } else if (authService.isUserLoggedIn()) {
        log.debug("Suppressed misleading 403 toast for an authenticated user", {
          path: stripQueryAndFragment(requestUrl),
        });
      } else {
        messageService.add({
          key: "global",
          severity: "error",
          summary: "Access Denied",
          detail: detail("You do not have permission to access this resource."),
          life: 7000,
        });
      }

      break;
    case 404:
      messageService.add({
        key: "global",
        severity: "error",
        summary: "Not Found",
        detail: detail("The requested resource was not found."),
        life: 7000,
      });
      break;
    case 409:
      messageService.add({
        key: "global",
        severity: "error",
        summary: translateOr(transloco, "ui.common.error.CONFLICT", "Conflict"),
        detail: detail("The request could not be completed due to a conflict."),
        life: 7000,
      });
      break;
    case 500:
      messageService.add({
        key: "global",
        severity: "error",
        summary: "Server Error",
        detail: "Something went wrong. Please try again later.",
        life: 7000,
      });
      break;
    case 503:
      messageService.add({
        key: "global",
        severity: "error",
        summary: "Service Unavailable",
        detail:
          "Service is temporarily unavailable. Please try again later.",
        life: 7000,
      });
      break;
    default:
      if (err.status >= 400) {
        messageService.add({
          key: "global",
          severity: "error",
          summary: "Something Went Wrong. Please Try Again.",
          life: 7000,
        });
      }
      break;
  }
}

export const responseInterceptor: HttpInterceptorFn = (req, next) => {
  const authService = inject(AuthService);
  const messageService = inject(MessageService);
  const transloco = inject(TranslocoService);
  const translationService = inject(TranslationService);
  const log = inject(LoggerService).createContext("ResponseInterceptor");
  const perf = inject(PerformanceMonitorService);
  const startTime = performance.now();

  return next(req).pipe(
    tap({
      next: (response: any) => {
        if (response.type === HttpEventType.Response) {
          // Record frontend-observed API latency (no-op unless RUM is enabled).
          perf.recordApiLatency(
            toApiRouteTemplate(req.url),
            performance.now() - startTime,
            response.status
          );
          return response;
        }
      },
      error: (err: HttpErrorResponse) => {
        // Record API latency for failed calls too (no-op unless RUM is enabled),
        // so error latency/volume is visible per status alongside successes.
        perf.recordApiLatency(
          toApiRouteTemplate(req.url),
          performance.now() - startTime,
          err.status
        );

        // Server-side (5xx) and network/timeout (status 0) failures go to the
        // error tracker via the LoggerService remote seam — independent of the
        // toast handling below, and even when a component opts out of global
        // error handling. Status + query-stripped path only; never the error
        // body or full URL (PII). 4xx are expected/handled — not reported.
        if (err.status >= 500 || err.status === 0) {
          log.error("HTTP request failed", undefined, {
            status: err.status,
            path: stripQueryAndFragment(req.url),
          });
        }

        // Check if component wants to handle this error manually
        const skipGlobalErrorHandling = req.context.get(SKIP_GLOBAL_ERROR);
        const showBackendErrorMessage = req.context.get(
          SHOW_BACKEND_ERROR_MESSAGE
        );

        // Extract error message from the API response format
        const errorMessage = extractBackendMessage(
          err,
          showBackendErrorMessage,
          log
        );

        if (skipGlobalErrorHandling) {
          return;
        }

        handleGlobalError(err, {
          messageService,
          authService,
          transloco,
          translationService,
          log,
          requestUrl: req.url,
          errorMessage,
          showBackendErrorMessage,
        });
      },
    })
  );
};
